import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { FunctionComponent, HTMLAttributes } from 'react';
import { faCopy, faKey } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Account } from '../types';
import { Modal } from '@/components';
import { toastFlashMessage } from '@/utils';

interface CloneApiKeyModalProps {
  open?: boolean;
  account?: Account;
  onClose?: () => void;
}

const Container: FunctionComponent = ({ children }) => (
  <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
    {children}
  </Flex>
);

const Title: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 's',
      padding: '12px 0',
      color: 't.black80',
      fontWeight: 600,
      fontSize: 'H600',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Flex>
);

const Message: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      width: '100%',
      padding: '7px 0 11px',
      color: 't.black80',
      fontWeight: 400,
      fontSize: 'H300',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Fields: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'column',
      gap: 'l',
      width: '100%',
    }}
  >
    {children}
  </Flex>
);

const FieldWrapper: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({
  children,
  ...props
}) => (
  <Box sx={{ position: 'relative', width: '100%' }} {...props}>
    {children}
  </Box>
);

const CopyButton: FunctionComponent<HTMLAttributes<HTMLDivElement>> = (
  props
) => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      right: '12px',
      color: 't.black70',
      fontWeight: 400,
      fontSize: 'H300',
      cursor: 'pointer',
      transform: 'translateY(-50%)',
    }}
    {...props}
  >
    <FontAwesomeIcon icon={faCopy} />
  </Box>
);

const Actions: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 'l',
      padding: '30px 0 40px',
    }}
  >
    {children}
  </Flex>
);

const CloneApiKeyModal: FunctionComponent<CloneApiKeyModalProps> = ({
  open = false,
  account,
  onClose,
}) => {
  const handleApiKeyCopy = () => {
    navigator.clipboard.writeText(account?.apiKey ?? '');
    toastFlashMessage('API Key has been copied to your clipboard', 'success');
  };

  const handleSecretCopy = () => {
    navigator.clipboard.writeText(account?.secret ?? '');
    toastFlashMessage('Secret has been copied to your clipboard', 'success');
  };

  return (
    <Modal open={open}>
      <Container>
        <Title>
          <Box as="span" sx={{ fontWeight: 400 }}>
            <FontAwesomeIcon icon={faKey} />
          </Box>
          <Box as="span">Clone API Key</Box>
        </Title>
        <Message>
          Once you close this window the secret key will no longer be available
          for access.
        </Message>
        <Fields>
          <FieldWrapper data-testid="cloneApiKeyModalLabel">
            <TextInput label="API Label" value={account?.displayName} />
          </FieldWrapper>
          <FieldWrapper data-testid="cloneApiKeyModalApiKey">
            <TextInput readOnly label="API Key" value={account?.apiKey} />
            <CopyButton onClick={handleApiKeyCopy} />
          </FieldWrapper>
          <FieldWrapper data-testid="cloneApiKeyModalSecret">
            <TextInput readOnly label="Secret Key" value={account?.secret} />
            <CopyButton onClick={handleSecretCopy} />
          </FieldWrapper>
        </Fields>
        <Actions>
          <Button onClick={onClose} data-testid="cloneApiKeyModalCloseButton">
            Close
          </Button>
        </Actions>
      </Container>
    </Modal>
  );
};

export default CloneApiKeyModal;
